import React, { useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  PictureAsPdfSharp as PictureAsPdfSharpIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  EmailOutlined as EmailOutlinedIcon,
  Edit as EditIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from "@mui/icons-material";
import EmailIcon from "@mui/icons-material/Email";
import { useTranslation } from "react-i18next";
import { TerminalCall } from "../../types/TerminalCall.ts";
import { v4 as uuid } from "uuid";
import dayjs from "dayjs";

export const DOCUMENT_ORIENTATION_PORTRAIT = "portrait";
export const DOCUMENT_ORIENTATION_LANDSCAPE = "landscape";
export const DOCUMENT_FIT_CONTAIN = "contain";
export const DOCUMENT_FIT_STRETCH = "stretch";

export type DocumentFitMode =
  | typeof DOCUMENT_FIT_CONTAIN
  | typeof DOCUMENT_FIT_STRETCH;

const PLAN_TYPE_LOADING = "loadingTerminalCall";
const PLAN_TYPE_LOADING_ONLY = "loading_only";
const TENANT_EMAIL_OPTION_PREFIX = "__tenant_email__:";

const DOCUMENT_ORIENTATIONS: Record<string, "p" | "l"> = {
  portrait: "p",
  landscape: "l",
};

export const createPdf = (orientation: string, format: string = "a4") => {
  return new jsPDF(
    DOCUMENT_ORIENTATIONS[orientation] ?? "p",
    "mm",
    format,
    true
  );
};

interface TerminalContainerPlannerButtonsProps {
  filename?: string;
  orientation?: string;
  pdfFormat?: string;
  pdfFitMode?: DocumentFitMode;
  pdfMargin?: number;
  variant?: "icon" | "button" | "card";
  action?: "pdf" | "email";
  containerRef?: React.RefObject<HTMLElement>;
  terminalCall?: TerminalCall;
  vessel?: any;
  planType?: string;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  emailRecipient?: string;
  currentTenant?: any;
  currentTenantEmail?: string;
}

type EmailRecipientOption = {
  id: string;
  name: string;
  email: string;
  contact?: any;
  isTenantFallback?: boolean;
};

const normalizeApiId = (value?: string) => {
  if (!value) {
    return "";
  }

  const parts = value.split("/");

  return parts[parts.length - 1];
};

const normalizeEmail = (value?: string | null) => {
  return value?.toString().trim() ?? "";
};

const getContactId = (contact: any) => {
  return contact?.["@id"] ?? contact?.id ?? "";
};

const getTenantEmailFromLocalStorage = () => {
  if (typeof window === "undefined") {
    return "";
  }

  const directKeys = [
    "tenantEmail",
    "currentTenantEmail",
    "selectedTenantEmail",
  ];

  for (const key of directKeys) {
    const value = normalizeEmail(window.localStorage.getItem(key));

    if (value) {
      return value;
    }
  }

  const objectKeys = ["tenant", "currentTenant", "selectedTenant"];

  for (const key of objectKeys) {
    const rawValue = window.localStorage.getItem(key);

    if (!rawValue) {
      continue;
    }

    try {
      const parsedValue = JSON.parse(rawValue);
      const email = normalizeEmail(
        parsedValue?.email ??
          parsedValue?.mail ??
          parsedValue?.contactEmail ??
          parsedValue?.tenantEmail
      );

      if (email) {
        return email;
      }
    } catch {
      // Ignore invalid localStorage JSON.
    }
  }

  return "";
};

const getReadableErrorMessage = async (
  response: Response,
  fallbackMessage: string
) => {
  try {
    const responseData = await response.json();

    return (
      responseData?.message ??
      responseData?.details ??
      responseData?.error?.message ??
      responseData?.error?.details ??
      fallbackMessage
    );
  } catch {
    try {
      const text = await response.text();

      return text || fallbackMessage;
    } catch {
      return fallbackMessage;
    }
  }
};

const TerminalContainerPlannerButtons: React.FC<
  TerminalContainerPlannerButtonsProps
> = ({
  filename,
  orientation = DOCUMENT_ORIENTATION_PORTRAIT,
  pdfFormat = "a4",
  pdfFitMode = DOCUMENT_FIT_CONTAIN,
  pdfMargin = 10,
  variant = "icon",
  action = "pdf",
  containerRef,
  terminalCall,
  vessel,
  planType,
  children,
  sx,
  disabled = false,
  emailRecipient,
  currentTenant,
  currentTenantEmail,
}) => {
  const { t } = useTranslation();

  const contacts = useMemo(() => {
    return terminalCall?.terminal?.contacts ?? [];
  }, [terminalCall]);

  const contactsWithEmail = useMemo(() => {
    return contacts.filter((contact: any) => normalizeEmail(contact?.email));
  }, [contacts]);

  const tenantFallbackEmail = useMemo(() => {
    return (
      normalizeEmail(currentTenantEmail) ||
      normalizeEmail(currentTenant?.email) ||
      normalizeEmail(currentTenant?.mail) ||
      normalizeEmail(currentTenant?.contactEmail) ||
      normalizeEmail(currentTenant?.tenantEmail) ||
      normalizeEmail((terminalCall as any)?.tenant?.email) ||
      normalizeEmail((terminalCall as any)?.currentTenant?.email) ||
      normalizeEmail((terminalCall as any)?.terminal?.tenant?.email) ||
      getTenantEmailFromLocalStorage()
    );
  }, [currentTenantEmail, currentTenant, terminalCall]);

  const recipientOptions = useMemo<EmailRecipientOption[]>(() => {
    if (contactsWithEmail.length > 0) {
      return contactsWithEmail.map((contact: any) => {
        const contactId = getContactId(contact);
        const email = normalizeEmail(contact.email);

        return {
          id: contactId,
          name: contact.name ?? email,
          email,
          contact,
          isTenantFallback: false,
        };
      });
    }

    if (tenantFallbackEmail) {
      return [
        {
          id: `${TENANT_EMAIL_OPTION_PREFIX}${tenantFallbackEmail}`,
          name: t("currentTenant"),
          email: tenantFallbackEmail,
          isTenantFallback: true,
        },
      ];
    }

    return [];
  }, [contactsWithEmail, tenantFallbackEmail, t]);

  const defaultRecipientId = recipientOptions[0]?.id ?? "";
  const canEditContact = recipientOptions.length > 1;

  const [selectedRecipientId, setSelectedRecipientId] =
    useState(defaultRecipientId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [sendStatus, setSendStatus] = useState("");
  const [sendError, setSendError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [sentRecipient, setSentRecipient] = useState("");

  useEffect(() => {
    if (!recipientOptions.length) {
      setSelectedRecipientId("");
      return;
    }

    const selectedRecipientStillExists = recipientOptions.some(
      (option) => option.id === selectedRecipientId
    );

    if (!selectedRecipientId || !selectedRecipientStillExists) {
      setSelectedRecipientId(defaultRecipientId);
    }
  }, [recipientOptions, defaultRecipientId, selectedRecipientId]);

  const selectedRecipient = useMemo(() => {
    return recipientOptions.find((option) => option.id === selectedRecipientId);
  }, [recipientOptions, selectedRecipientId]);

  const resolvedEmailRecipient =
    emailRecipient ?? selectedRecipient?.email ?? t("noEmail");

  const emailDisabled =
    disabled ||
    !terminalCall ||
    !vessel ||
    recipientOptions.length === 0 ||
    sending;

  const createScaledPdf = async (
    htmlContainer: HTMLElement,
    pdfOrientation: string,
    pdfFormat: string = "a4"
  ) => {
    const pdf = createPdf(pdfOrientation, pdfFormat);

    const padding = Math.max(0, pdfMargin);
    const pdfPageWidth = pdf.internal.pageSize.getWidth();
    const pdfPageHeight = pdf.internal.pageSize.getHeight();

    const contentWidth = htmlContainer.scrollWidth;
    const contentHeight = htmlContainer.scrollHeight;

    const availableWidth = pdfPageWidth - padding * 2;
    const availableHeight = pdfPageHeight - padding * 2;

    const scale = Math.min(
      availableWidth / contentWidth,
      availableHeight / contentHeight
    );
    const scaledWidth =
      pdfFitMode === DOCUMENT_FIT_STRETCH
        ? availableWidth
        : contentWidth * scale;
    const scaledHeight =
      pdfFitMode === DOCUMENT_FIT_STRETCH
        ? availableHeight
        : contentHeight * scale;

    const offsetX = (pdfPageWidth - scaledWidth) / 2;
    const offsetY = (pdfPageHeight - scaledHeight) / 2;

    const canvas = await html2canvas(htmlContainer, {
      backgroundColor: "#ffffff",
      height: contentHeight,
      logging: false,
      scale: 2,
      scrollX: -window.scrollX,
      scrollY: -window.scrollY,
      useCORS: true,
      width: contentWidth,
      windowHeight: Math.max(document.documentElement.clientHeight, contentHeight),
      windowWidth: Math.max(document.documentElement.clientWidth, contentWidth),
    });

    pdf.addImage(
      canvas.toDataURL("image/png"),
      "PNG",
      offsetX,
      offsetY,
      scaledWidth,
      scaledHeight,
      undefined,
      "FAST"
    );

    return pdf;
  };

  const savePdf = async () => {
    const htmlContainer = containerRef?.current;

    if (!htmlContainer) {
      console.warn(t("pdfContainerNotFound"));
      return;
    }

    const pdf = await createScaledPdf(
      htmlContainer,
      orientation,
      pdfFormat
    );

    pdf.save(`${filename ?? uuid()}.pdf`);
  };

  const sendPlanningEmail = async ({
    fileId,
    subject,
    recipient,
  }: {
    fileId: string;
    subject: string;
    recipient: EmailRecipientOption;
  }) => {
    const terminalCallId = normalizeApiId(terminalCall?.["@id"]);
    const vesselId = normalizeApiId(vessel?.["@id"]);

    if (!fileId || !terminalCallId || !vesselId) {
      throw new Error(t("missingMailPlanningIdentifiers"));
    }

    if (recipient.isTenantFallback) {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/email_planning_by_email/${fileId}/${terminalCallId}/${vesselId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            email: recipient.email,
            subject,
          }),
        }
      );

      if (!response.ok) {
        const message = await getReadableErrorMessage(
          response,
          t("failedToSendPlanningEmail")
        );

        throw new Error(message);
      }

      return;
    }

    const contactId = normalizeApiId(recipient.id);

    if (!contactId) {
      throw new Error(t("missingContactIdentifier"));
    }

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/email_planning/${encodeURIComponent(
        subject
      )}/${contactId}/${fileId}/${terminalCallId}/${vesselId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (!response.ok) {
      const message = await getReadableErrorMessage(
        response,
        t("failedToSendPlanningEmail")
      );

      throw new Error(message);
    }
  };

  const mailPlanning = async (recipient: EmailRecipientOption) => {
    if (!recipient || !recipient.email || !terminalCall || !vessel) {
      throw new Error(t("missingRecipientTerminalCallOrVessel"));
    }

    const htmlContainer = containerRef?.current;

    if (!htmlContainer) {
      throw new Error(t("pdfContainerNotFound"));
    }

    setSendError(null);
    setSendStatus(t("generatingPdf"));
    setSendProgress(10);

    const pdf = await createScaledPdf(
      htmlContainer,
      DOCUMENT_ORIENTATION_LANDSCAPE,
      pdfFormat
    );

    setSendStatus(t("preparingAttachment"));
    setSendProgress(35);

    const savedDocument = new Blob([pdf.output("blob")], {
      type: "application/pdf",
    });

    const formData = new FormData();

    formData.append("file", savedDocument, `${uuid()}.pdf`);

    setSendStatus(t("uploadingPdf"));
    setSendProgress(55);

    const uploadResponse = await fetch(
      `${process.env.REACT_APP_API_URL}/files`,
      {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (!uploadResponse.ok) {
      const message = await getReadableErrorMessage(
        uploadResponse,
        t("failedToUploadPdfFile")
      );

      throw new Error(message);
    }

    const uploadedFile = await uploadResponse.json();
    const fileId = normalizeApiId(uploadedFile["@id"]);

    const subject = `${vessel.name} - ${
      planType === PLAN_TYPE_LOADING || planType === PLAN_TYPE_LOADING_ONLY
        ? t("loading")
        : t("unloading")
    } - ${terminalCall.terminal.name} - ${dayjs(terminalCall?.planned).format(
      "DD-MM-YYYY"
    )}`;

    setSendStatus(t("sendingEmail"));
    setSendProgress(80);

    await sendPlanningEmail({
      fileId,
      subject,
      recipient,
    });

    setSendStatus(t("emailSent"));
    setSendProgress(100);
  };

  const openConfirmDialog = () => {
    if (emailDisabled) {
      return;
    }

    setSendError(null);
    setEditContactOpen(false);
    setConfirmOpen(true);
  };

  const closeConfirmDialog = () => {
    if (sending) {
      return;
    }

    setConfirmOpen(false);
    setEditContactOpen(false);
    setSendError(null);
    setSendProgress(0);
    setSendStatus("");
  };

  const handleContactChange = (event: SelectChangeEvent<string>) => {
    setSelectedRecipientId(event.target.value);
    setEmailSent(false);
    setSentRecipient("");
  };

  const handleSendEmail = async () => {
    if (!selectedRecipient) {
      setSendError(t("noValidEmailRecipientSelected"));
      return;
    }

    setSending(true);
    setEmailSent(false);
    setSentRecipient("");
    setSendError(null);
    setSendProgress(5);
    setSendStatus(t("starting"));

    try {
      await mailPlanning(selectedRecipient);

      setEmailSent(true);
      setSentRecipient(selectedRecipient.email);
      setSending(false);

      setTimeout(() => {
        setConfirmOpen(false);
        setEditContactOpen(false);
        setSendProgress(0);
        setSendStatus("");
      }, 700);
    } catch (error: any) {
      console.error(error);
      setEmailSent(false);
      setSending(false);
      setSendProgress(0);
      setSendStatus("");
      setSendError(error?.message ?? t("failedToSendPlanningEmail"));
    }
  };

  const handleClick = () => {
    if (disabled) {
      return;
    }

    if (action === "email") {
      openConfirmDialog();
      return;
    }

    savePdf();
  };

  if (variant === "card") {
    const isEmail = action === "email";
    const emailIconIsSuccess = isEmail && emailSent;

    return (
      <>
        <ButtonBase
          onClick={handleClick}
          disabled={isEmail ? emailDisabled : disabled}
          sx={{
            width: "100%",
            minWidth: {
              xs: "100%",
              md: 300,
            },
            maxWidth: {
              xs: "100%",
              md: 420,
            },
            height: 96,
            borderRadius: 2,
            border: "1px solid",
            borderColor: emailIconIsSuccess
              ? "success.main"
              : sendError && isEmail
                ? "error.main"
                : "divider",
            backgroundColor: "background.paper",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 2,
            px: 2,
            py: 1.75,
            textAlign: "left",
            opacity: isEmail && emailDisabled ? 0.5 : disabled ? 0.5 : 1,
            cursor:
              isEmail && emailDisabled
                ? "not-allowed"
                : disabled
                  ? "not-allowed"
                  : "pointer",
            transition:
              "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
            "&:hover": {
              borderColor: emailIconIsSuccess
                ? "success.main"
                : isEmail
                  ? "primary.main"
                  : "error.main",
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
              transform: "translateY(-1px)",
            },
            ...sx,
          }}
        >
          {children ?? (
            <>
              <Avatar
                sx={{
                  width: 62,
                  height: 62,
                  flex: "0 0 auto",
                  backgroundColor: emailIconIsSuccess
                    ? "rgba(46, 125, 50, 0.1)"
                    : isEmail
                      ? "rgba(25, 118, 210, 0.08)"
                      : "rgba(211, 47, 47, 0.08)",
                  color: emailIconIsSuccess
                    ? "success.main"
                    : isEmail
                      ? "primary.main"
                      : "error.main",
                }}
              >
                {emailIconIsSuccess ? (
                  <CheckCircleOutlineIcon sx={{ fontSize: 34 }} />
                ) : isEmail ? (
                  <EmailOutlinedIcon sx={{ fontSize: 34 }} />
                ) : (
                  <DescriptionOutlinedIcon sx={{ fontSize: 34 }} />
                )}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={800}
                  sx={{
                    lineHeight: 1.2,
                    color: "text.primary",
                  }}
                >
                  {isEmail
                    ? emailSent
                      ? t("emailSent")
                      : t("sendByEmail")
                    : t("generatePdf")}
                </Typography>

                <Typography
                  variant="body2"
                  color={emailIconIsSuccess ? "success.main" : "text.secondary"}
                  noWrap
                  sx={{
                    mt: 0.5,
                  }}
                >
                  {isEmail
                    ? emailSent
                      ? t("sentTo", {
                          recipient: sentRecipient || resolvedEmailRecipient,
                        })
                      : t("emailTo", {
                          recipient: resolvedEmailRecipient,
                        })
                    : t("exportPlanningAsPdf")}
                </Typography>
              </Box>

              {isEmail && canEditContact ? (
                <Tooltip title={t("changeContact")}>
                  <IconButton
                    size="small"
                    disabled={sending}
                    onClick={(event) => {
                      event.stopPropagation();

                      if (!emailDisabled) {
                        setEditContactOpen(true);
                        setConfirmOpen(true);
                      }
                    }}
                  >
                    <EditIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </>
          )}
        </ButtonBase>

        {isEmail ? (
          <Dialog
            open={confirmOpen}
            onClose={closeConfirmDialog}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>{t("sendPlanningByEmail")}</DialogTitle>

            <DialogContent>
              <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("sendPlanningPdfTo")}
                </Typography>

                {!editContactOpen ? (
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={800} noWrap>
                        {selectedRecipient?.name ?? t("recipient")}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" noWrap>
                        {selectedRecipient?.email ?? t("noEmail")}
                      </Typography>
                    </Box>

                    {canEditContact ? (
                      <Button
                        size="small"
                        onClick={() => setEditContactOpen(true)}
                        disabled={sending}
                      >
                        {t("edit")}
                      </Button>
                    ) : null}
                  </Box>
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel id="terminal-planning-contact-label">
                      {t("contact")}
                    </InputLabel>

                    <Select
                      labelId="terminal-planning-contact-label"
                      value={selectedRecipientId}
                      label={t("contact")}
                      onChange={handleContactChange}
                      disabled={sending}
                    >
                      {recipientOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name
                            ? `${option.name} - ${option.email}`
                            : option.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Typography variant="caption" color="text.secondary">
                  {t("pdfGeneratedAndAttachedAutomatically")}
                </Typography>

                {sending ? (
                  <Box sx={{ width: "100%" }}>
                    <LinearProgress
                      variant="determinate"
                      value={sendProgress}
                      sx={{
                        height: 6,
                        borderRadius: 999,
                      }}
                    />

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mt: 0.75,
                      }}
                    >
                      {sendStatus || t("sendingWithProgress", { progress: sendProgress })}
                    </Typography>
                  </Box>
                ) : null}

                {emailSent && !sending ? (
                  <Alert severity="success">
                    {t("emailSentTo", {
                      recipient: sentRecipient || resolvedEmailRecipient,
                    })}
                  </Alert>
                ) : null}

                {sendError ? <Alert severity="error">{sendError}</Alert> : null}
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={closeConfirmDialog} disabled={sending}>
                {emailSent ? t("close") : t("cancel")}
              </Button>

              <Button
                onClick={handleSendEmail}
                variant="contained"
                disabled={sending || !selectedRecipientId || emailSent}
                color={emailSent ? "success" : "primary"}
              >
                {sending
                  ? t("sending")
                  : emailSent
                    ? t("sent")
                    : t("sendEmail")}
              </Button>
            </DialogActions>
          </Dialog>
        ) : null}
      </>
    );
  }

  if (variant === "button") {
    return (
      <>
        <Button
          onClick={savePdf}
          startIcon={<PictureAsPdfSharpIcon />}
          variant="outlined"
          disabled={disabled}
        >
          {t("generatePdf")}
        </Button>

        {terminalCall ? (
          <Button
            onClick={openConfirmDialog}
            disabled={emailDisabled}
            color={emailSent ? "success" : "primary"}
            startIcon={
              emailSent ? (
                <CheckCircleOutlineIcon fontSize="inherit" />
              ) : (
                <EmailIcon fontSize="inherit" />
              )
            }
          >
            {emailSent ? t("sent") : t("email")}
          </Button>
        ) : null}
      </>
    );
  }

  return (
    <>
      <IconButton onClick={savePdf} disabled={disabled}>
        <PictureAsPdfSharpIcon />
      </IconButton>

      {terminalCall ? (
        <IconButton
          onClick={openConfirmDialog}
          disabled={emailDisabled}
          color={emailSent ? "success" : "default"}
        >
          {emailSent ? <CheckCircleOutlineIcon /> : <EmailIcon />}
        </IconButton>
      ) : null}
    </>
  );
};

export default TerminalContainerPlannerButtons;
